/**
 * Merchant Configurations Service
 *
 * Data access layer for merchant_configurations table.
 * Handles return app configuration operations.
 */

import { handleSupabaseError } from '../lib/supabase';
import type {
  MerchantConfiguration,
  FlowConfig,
  Section,
  Question,
  QuestionOption,
  BilingualText,
} from '../types/merchant-configurations';

// Re-export types for convenience
export type { MerchantConfiguration, FlowConfig };

// Validation error interface
export interface ValidationError {
  path: string;
  message: string;
}

// Errors block saving; warnings (e.g. missing optional translations) are
// surfaced to the merchant but do not block.
export interface ValidationResult {
  errors: ValidationError[];
  warnings: ValidationError[];
}

interface ValidationContext {
  // Merchant's supported languages; used to warn about untranslated texts.
  merchantLanguages: string[];
}

/**
 * Fetch merchant configuration
 */
export async function getMerchantConfiguration(merchantId: string): Promise<{
  data: MerchantConfiguration | null;
  error: string | null;
}> {
  try {
    const response = await fetch(`/api/merchant-configurations?merchantId=${merchantId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.message || 'Failed to fetch configuration');
    }

    return { data: result.data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create merchant configuration
 */
export async function createMerchantConfiguration(
  merchantId: string,
  flowConfig: FlowConfig,
  autoCompleteBooking: boolean = true,
  allowReturnUpdates: boolean = true
): Promise<{
  data: MerchantConfiguration | null;
  error: string | null;
}> {
  try {
    const response = await fetch('/api/merchant-configurations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        merchantId,
        flowConfig,
        autoCompleteBooking,
        allowReturnUpdates,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.message || 'Failed to create configuration');
    }

    return { data: result.data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update merchant configuration
 */
export async function updateMerchantConfiguration(
  merchantId: string,
  updates: {
    flowConfig?: FlowConfig;
    autoCompleteBooking?: boolean;
    allowReturnUpdates?: boolean;
  }
): Promise<{
  data: MerchantConfiguration | null;
  error: string | null;
}> {
  try {
    const response = await fetch('/api/merchant-configurations', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        merchantId,
        ...updates,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.message || 'Failed to update configuration');
    }

    return { data: result.data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

function mergeResults(target: ValidationResult, ...sources: ValidationResult[]): ValidationResult {
  for (const source of sources) {
    target.errors.push(...source.errors);
    target.warnings.push(...source.warnings);
  }
  return target;
}

function emptyResult(): ValidationResult {
  return { errors: [], warnings: [] };
}

function hasLang(text: BilingualText | null | undefined, lang: string): boolean {
  return typeof text?.[lang] === 'string' && text[lang].trim() !== '';
}

/**
 * Validate a multilingual text object.
 *
 * Validation runs on the save-path-normalized config (see
 * lib/flowConfigNormalization.ts), which guarantees "en" whenever any
 * language has content. So the errors below only fire for texts that are
 * empty in every language (the blank-text hard floor). Untranslated
 * merchant-enabled languages are reported as non-blocking warnings.
 */
function validateMultilingualText(
  text: BilingualText | null | undefined,
  path: string,
  ctx: ValidationContext,
  required: boolean = true
): ValidationResult {
  const result = emptyResult();

  const filledLangs = text && typeof text === 'object'
    ? Object.keys(text).filter((lang) => hasLang(text, lang))
    : [];

  if (filledLangs.length === 0) {
    if (required) {
      result.errors.push({ path, message: 'Text is required in at least one language' });
    }
    return result;
  }

  if (!hasLang(text, 'en')) {
    result.errors.push({ path: `${path}.en`, message: 'English text is required' });
  }

  for (const lang of ctx.merchantLanguages) {
    if (!hasLang(text, lang)) {
      result.warnings.push({
        path: `${path}.${lang}`,
        message: `Missing ${lang.toUpperCase()} translation`,
      });
    }
  }

  return result;
}

/**
 * Validate a question option
 */
function validateOption(
  option: QuestionOption,
  path: string,
  existingValues: Set<string>,
  ctx: ValidationContext
): ValidationResult {
  const result = emptyResult();

  if (!option.value?.trim()) {
    result.errors.push({ path: `${path}.value`, message: 'Option value is required' });
  } else if (existingValues.has(option.value)) {
    result.errors.push({ path: `${path}.value`, message: 'Option values must be unique within a question' });
  }

  mergeResults(result, validateMultilingualText(option.label, `${path}.label`, ctx));

  const validActions = ['proceed', 'block', 'require_details'];
  if (!validActions.includes(option.action)) {
    result.errors.push({ path: `${path}.action`, message: 'Invalid action type' });
  }

  if (option.action === 'block') {
    if (!option.blocking_message) {
      result.errors.push({ path: `${path}.blocking_message`, message: 'Blocking message is required when action is "block"' });
    } else {
      mergeResults(result, validateMultilingualText(option.blocking_message, `${path}.blocking_message`, ctx));
    }
  }

  if (option.action === 'require_details') {
    if (!option.details_config) {
      result.errors.push({ path: `${path}.details_config`, message: 'Details config is required when action is "require_details"' });
    } else {
      if (!option.details_config.field_name?.trim()) {
        result.errors.push({ path: `${path}.details_config.field_name`, message: 'Field name is required' });
      }
      mergeResults(result, validateMultilingualText(option.details_config.placeholder, `${path}.details_config.placeholder`, ctx, false));
    }
  }

  return result;
}

/**
 * Validate a question
 */
function validateQuestion(question: Question, path: string, ctx: ValidationContext): ValidationResult {
  const result = emptyResult();

  if (!question.id?.trim()) {
    result.errors.push({ path: `${path}.id`, message: 'Question ID is required' });
  }

  if (question.order === undefined || question.order === null) {
    result.errors.push({ path: `${path}.order`, message: 'Question order is required' });
  }

  mergeResults(result, validateMultilingualText(question.text, `${path}.text`, ctx));
  mergeResults(result, validateMultilingualText(question.preamble, `${path}.preamble`, ctx, false));

  const validTypes = ['radio', 'checkbox', 'text', 'textarea'];
  if (!validTypes.includes(question.type)) {
    result.errors.push({ path: `${path}.type`, message: 'Invalid question type' });
  }

  // Radio and checkbox require options
  if ((question.type === 'radio' || question.type === 'checkbox') && (!question.options || question.options.length === 0)) {
    result.errors.push({ path: `${path}.options`, message: 'Radio/checkbox questions must have at least one option' });
  }

  // Validate options
  if (question.options && question.options.length > 0) {
    const existingValues = new Set<string>();
    question.options.forEach((option, optIndex) => {
      mergeResults(result, validateOption(option, `${path}.options[${optIndex}]`, existingValues, ctx));
      if (option.value?.trim()) {
        existingValues.add(option.value);
      }
    });
  }

  return result;
}

const VALID_PAGINATION_MODES = ['one_per_page', 'accordion', 'all_at_once'];
const VALID_ITEM_PHOTO_MODES = ['optional', 'required', 'required_if_issues'];

/**
 * Validate a section
 */
function validateSection(section: Section, path: string, ctx: ValidationContext): ValidationResult {
  const result = emptyResult();

  if (!section.id?.trim()) {
    result.errors.push({ path: `${path}.id`, message: 'Section ID is required' });
  }

  const validTypes = ['photo_upload', 'question_set', 'completion'];
  if (!validTypes.includes(section.type)) {
    result.errors.push({ path: `${path}.type`, message: 'Invalid section type' });
  }

  if (typeof section.order !== 'number') {
    result.errors.push({ path: `${path}.order`, message: 'Section order must be a number' });
  }

  if (section.enabled === undefined) {
    result.errors.push({ path: `${path}.enabled`, message: 'Section enabled flag is required' });
  }

  // Type-specific validation
  if (section.type === 'photo_upload') {
    const config = section.config;
    if (config.min_photos !== undefined && config.max_photos !== undefined) {
      if (config.min_photos > config.max_photos) {
        result.errors.push({ path: `${path}.config`, message: 'min_photos cannot be greater than max_photos' });
      }
    }
    if (config.max_photos !== undefined && config.max_photos > 50) {
      result.errors.push({ path: `${path}.config.max_photos`, message: 'max_photos cannot exceed 50' });
    }

    // The return app validates these texts whenever the key is present, so
    // they must satisfy the language contract even though they're optional.
    mergeResults(result, validateMultilingualText(config.header_text, `${path}.config.header_text`, ctx, false));
    mergeResults(result, validateMultilingualText(config.instructions, `${path}.config.instructions`, ctx, false));
  }

  if (section.type === 'question_set') {
    const config = section.config;

    if (!config.scope) {
      result.errors.push({ path: `${path}.config.scope`, message: 'Question section scope is required' });
    } else if (!['booking', 'item'].includes(config.scope)) {
      result.errors.push({ path: `${path}.config.scope`, message: 'Scope must be "booking" or "item"' });
    }

    mergeResults(result, validateMultilingualText(config.header_text, `${path}.config.header_text`, ctx, false));
    mergeResults(result, validateMultilingualText(config.instructions, `${path}.config.instructions`, ctx, false));

    // Validate questions (max 5)
    if (config.questions && config.questions.length > 5) {
      result.errors.push({ path: `${path}.config.questions`, message: 'Maximum 5 questions per section' });
    }

    config.questions?.forEach((question, qIndex) => {
      mergeResults(result, validateQuestion(question, `${path}.config.questions[${qIndex}]`, ctx));
    });

    if (config.pagination !== undefined && !VALID_PAGINATION_MODES.includes(config.pagination)) {
      result.errors.push({ path: `${path}.config.pagination`, message: `Invalid pagination mode "${config.pagination}"` });
    }

    // Validate item photos config if present
    if (config.item_photos_config) {
      const photoConfig = config.item_photos_config;
      if (photoConfig.mode !== undefined && !VALID_ITEM_PHOTO_MODES.includes(photoConfig.mode)) {
        result.errors.push({ path: `${path}.config.item_photos_config.mode`, message: `Invalid item photo mode "${photoConfig.mode}"` });
      }
      if (photoConfig.enabled && photoConfig.min_photos > photoConfig.max_photos) {
        result.errors.push({ path: `${path}.config.item_photos_config`, message: 'min_photos cannot be greater than max_photos' });
      }
      mergeResults(result, validateMultilingualText(photoConfig.instructions, `${path}.config.item_photos_config.instructions`, ctx, false));
    }
  }

  if (section.type === 'completion') {
    const config = section.config;
    mergeResults(result, validateMultilingualText(config.header_text, `${path}.config.header_text`, ctx));
    mergeResults(result, validateMultilingualText(config.message, `${path}.config.message`, ctx));

    if (config.review_request) {
      const rrPath = `${path}.config.review_request`;
      mergeResults(result, validateMultilingualText(config.review_request.text, `${rrPath}.text`, ctx, false));
      mergeResults(result, validateMultilingualText(config.review_request.button_text, `${rrPath}.button_text`, ctx, false));

      // An enabled review request with no text renders with app-default copy
      // only; worth flagging, but not a save blocker.
      if (config.review_request.enabled) {
        if (!config.review_request.text) {
          result.warnings.push({ path: `${rrPath}.text`, message: 'Review request is enabled but has no text' });
        }
        if (!config.review_request.button_text) {
          result.warnings.push({ path: `${rrPath}.button_text`, message: 'Review request is enabled but has no button text' });
        }
      }
    }

    // Validate instruction boxes (max 3)
    if (config.instruction_boxes && config.instruction_boxes.length > 3) {
      result.errors.push({ path: `${path}.config.instruction_boxes`, message: 'Maximum 3 instruction boxes allowed' });
    }

    config.instruction_boxes?.forEach((box, boxIndex) => {
      const boxPath = `${path}.config.instruction_boxes[${boxIndex}]`;
      mergeResults(result, validateMultilingualText(box.title, `${boxPath}.title`, ctx));
      mergeResults(result, validateMultilingualText(box.text, `${boxPath}.text`, ctx));

      if (box.order === undefined || box.order === null) {
        result.errors.push({ path: `${boxPath}.order`, message: 'Instruction box order is required' });
      }

      // location_ids is optional. When present it must be an array of
      // non-empty strings — the return-app's ConfigValidator falls back to
      // the default config if this is malformed, silently losing all custom
      // copy, so we catch it client-side first.
      if (box.location_ids !== undefined) {
        const fieldPath = `${boxPath}.location_ids`;
        if (!Array.isArray(box.location_ids)) {
          result.errors.push({ path: fieldPath, message: 'location_ids must be an array of location UUIDs' });
        } else if (box.location_ids.some((id) => typeof id !== 'string' || id.length === 0)) {
          result.errors.push({ path: fieldPath, message: 'location_ids entries must be non-empty strings' });
        }
      }
    });
  }

  return result;
}

/**
 * Validate the entire flow configuration.
 *
 * Meant to run on the output of normalizeFlowConfig (lib/flowConfigNormalization.ts)
 * so that auto-fillable language gaps have already been repaired and only
 * genuine problems are reported. Returns hard errors (block saving — the
 * return app would discard the config and serve its default flow) separately
 * from warnings (saved fine, but the merchant should know).
 */
export function validateFlowConfig(
  flowConfig: FlowConfig,
  options?: { merchantLanguages?: string[] }
): ValidationResult {
  const ctx: ValidationContext = {
    merchantLanguages: options?.merchantLanguages ?? [],
  };
  const result = emptyResult();

  if (!flowConfig.sections || !Array.isArray(flowConfig.sections)) {
    result.errors.push({ path: 'sections', message: 'Sections array is required' });
    return result;
  }

  // Must have at least one completion section
  const hasCompletion = flowConfig.sections.some(s => s.type === 'completion' && s.enabled);
  if (!hasCompletion) {
    result.errors.push({ path: 'sections', message: 'At least one enabled completion section is required' });
  }

  // Check for unique section IDs
  const sectionIds = new Set<string>();
  const sectionOrders = new Set<number>();

  flowConfig.sections.forEach((section, index) => {
    mergeResults(result, validateSection(section, `sections[${index}]`, ctx));

    if (section.id && sectionIds.has(section.id)) {
      result.errors.push({ path: `sections[${index}].id`, message: 'Section IDs must be unique' });
    }
    sectionIds.add(section.id);

    if (sectionOrders.has(section.order)) {
      result.errors.push({ path: `sections[${index}].order`, message: 'Section orders must be unique' });
    }
    sectionOrders.add(section.order);
  });

  return result;
}

/**
 * Format validation errors into a user-friendly message
 */
export function formatValidationErrors(errors: ValidationError[]): string {
  if (errors.length === 0) return '';

  if (errors.length === 1) {
    return `${errors[0].message} (${errors[0].path})`;
  }

  return `${errors.length} validation errors:\n${errors.map(e => `- ${e.message} (${e.path})`).join('\n')}`;
}
